import { useId, type ReactNode } from 'react'
import { Button, Icon, Text } from '@/design-system'
import { cn } from '@/lib/cn'
import styles from './FilterBar.module.css'

export interface SelectOption {
  readonly value: string
  readonly label: string
}

export function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string
  readonly value: string
  readonly options: readonly SelectOption[]
  readonly onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className={styles.select}>
      <label className={styles.selectLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={styles.control}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function FilterChip({
  children,
  on,
  onClick,
}: {
  readonly children: ReactNode
  readonly on: boolean
  readonly onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(styles.chip, on && styles.chipOn)}
      aria-pressed={on}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/**
 * Built from the same parts as FilterSelect rather than from Field: inside the
 * bar the search has to match the selects in height, border and label, and the
 * filled Field reads as a grey slab on a white card.
 */
function SearchInput({
  label,
  value,
  onChange,
}: {
  readonly label: string
  readonly value: string
  readonly onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className={cn(styles.select, styles.search)}>
      <label className={styles.selectLabel} htmlFor={id}>
        {label}
      </label>
      <div className={styles.searchControl}>
        <Icon name="search" size={18} />
        <input
          id={id}
          type="search"
          className={styles.searchInput}
          value={value}
          placeholder="Name, neighbourhood or city"
          onChange={(event) => onChange(event.target.value)}
        />
        {value && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear search"
            onClick={() => onChange('')}
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

export interface FilterBarProps {
  readonly searchLabel: string
  readonly searchValue: string
  readonly onSearch: (value: string) => void
  readonly selects: ReactNode
  readonly chips?: ReactNode
  /** Result count, so the filters always say what they did. */
  readonly count: number
  readonly noun: string
  readonly onReset?: (() => void) | undefined
  readonly dirty: boolean
}

export function FilterBar({
  searchLabel,
  searchValue,
  onSearch,
  selects,
  chips,
  count,
  noun,
  onReset,
  dirty,
}: FilterBarProps) {
  return (
    <div className={styles.bar}>
      <div className={styles.top}>
        <SearchInput label={searchLabel} value={searchValue} onChange={onSearch} />
        <div className={styles.selects}>{selects}</div>
      </div>

      {chips && <div className={styles.chips}>{chips}</div>}

      <div className={styles.summary}>
        <Text variant="caption" tone="secondary" aria-live="polite">
          {count} {count === 1 ? noun : `${noun}s`}
          {dirty ? ' match your filters' : ' available'}
        </Text>
        {dirty && onReset && (
          <Button variant="ghost" size="sm" iconStart="close" onClick={onReset}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  )
}
